import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import {
  Store, Package, GraduationCap, Users as P2PIcon, Home, Truck,
  CheckCircle, XCircle, Archive, RefreshCw, DollarSign, Clock,
  ShoppingBag, Loader2, Eye, X, Tag, Sparkles, Star, Settings as SettingsIcon,
  TrendingUp, Search, Layers,
} from 'lucide-react';

const TYPE_META = {
  course:            { label: 'Course',    icon: GraduationCap, tone: 'indigo'  },
  gear:              { label: 'Gear',      icon: Package,       tone: 'emerald' },
  p2p_service:       { label: 'Service',   icon: P2PIcon,       tone: 'violet'  },
  housing:           { label: 'Housing',   icon: Home,          tone: 'rose'    },
  equipment_rental:  { label: 'Rental',    icon: Truck,         tone: 'amber'   },
};

const TONE = {
  indigo:  { bg: 'bg-indigo-50', text: 'text-indigo-700', ring: 'ring-indigo-500/20', dot: 'bg-indigo-500', grad: 'from-indigo-500 to-blue-500' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-500/20', dot: 'bg-emerald-500', grad: 'from-emerald-500 to-teal-500' },
  violet:  { bg: 'bg-violet-50', text: 'text-violet-700', ring: 'ring-violet-500/20', dot: 'bg-violet-500', grad: 'from-violet-500 to-fuchsia-500' },
  rose:    { bg: 'bg-rose-50', text: 'text-rose-700', ring: 'ring-rose-500/20', dot: 'bg-rose-500', grad: 'from-rose-500 to-pink-500' },
  amber:   { bg: 'bg-amber-50', text: 'text-amber-800', ring: 'ring-amber-500/20', dot: 'bg-amber-500', grad: 'from-amber-500 to-orange-500' },
  slate:   { bg: 'bg-slate-50', text: 'text-slate-700', ring: 'ring-slate-500/20', dot: 'bg-slate-500', grad: 'from-slate-600 to-slate-800' },
};

const STATUS_PILL = {
  pending_review: { cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200', label: 'Pending' },
  active:         { cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200', label: 'Active' },
  rejected:       { cls: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200', label: 'Rejected' },
  archived:       { cls: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200', label: 'Archived' },
  paused:         { cls: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200', label: 'Paused' },
};

function PremiumStat({ icon, label, value, hint, tone = 'indigo' }) {
  const t = TONE[tone];
  return (
    <div className="relative bg-white rounded-2xl border border-slate-200/80 p-4 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div className={`absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br ${t.grad} opacity-10 rounded-full`} />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
        </div>
        <div className={`p-2 rounded-xl bg-gradient-to-br ${t.grad} text-white shadow-md`}>{icon}</div>
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, children, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
        active
          ? 'bg-slate-900 text-white shadow-sm ring-1 ring-slate-900'
          : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-slate-400 hover:text-slate-900'
      }`}
    >
      {children}
      {count > 0 && (
        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none ${
          active ? 'bg-white/20 text-white' : 'bg-amber-500 text-white'
        }`}>
          {count}
        </span>
      )}
    </button>
  );
}

function CommissionSettingsModal({ open, current, onClose, onSaved }) {
  const [pct, setPct] = useState(current ?? 5);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPct(current ?? 5); }, [current, open]);
  if (!open) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await apiCall('/api/admin/marketplace/settings', {
        method: 'PUT',
        body: JSON.stringify({ commission_pct: parseFloat(pct) }),
      });
      toast.success('Commission updated');
      onSaved();
      onClose();
    } catch (e) {
      toast.error('Save failed', { description: e.message });
    }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="commission-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 text-white"><DollarSign className="w-5 h-5" /></div>
            <div>
              <h3 className="font-bold text-slate-900">Platform Commission</h3>
              <p className="text-xs text-slate-500">Take-rate on every marketplace order</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Commission %</span>
            <div className="mt-1.5 relative">
              <input
                type="number"
                step="0.5"
                min="0"
                max="50"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                className="w-full pl-3 pr-10 py-2.5 border border-slate-300 rounded-lg text-lg font-bold text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="commission-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold">%</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Range: 0–50%. Applies to new orders only.</p>
          </label>
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
            <p>On a <span className="font-bold text-slate-900">$100</span> order at <span className="font-bold text-emerald-600">{Number(pct || 0).toFixed(1)}%</span></p>
            <p className="mt-1">Platform earns <span className="font-bold text-emerald-700">${(100 * (parseFloat(pct) || 0) / 100).toFixed(2)}</span> · Seller receives <span className="font-bold text-slate-900">${(100 - 100 * (parseFloat(pct) || 0) / 100).toFixed(2)}</span></p>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 px-4 rounded-lg border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50">Cancel</button>
            <button
              onClick={submit}
              disabled={busy}
              className="flex-1 py-2.5 px-4 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5"
              data-testid="commission-save"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ listing, onClose, onModerate, onFeature, busyId }) {
  if (!listing) return null;
  const meta = TYPE_META[listing.type] || TYPE_META.gear;
  const t = TONE[meta.tone];
  const Icon = meta.icon;
  const isBusy = busyId === listing._id;
  const status = STATUS_PILL[listing.status] || STATUS_PILL.archived;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-listing-detail-modal">
        <div className={`relative h-32 bg-gradient-to-br ${t.grad}`}>
          {listing.images?.[0] ? (
            <img src={listing.images[0]} alt="" className="w-full h-full object-cover opacity-90" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center"><Icon className="w-14 h-14 text-white/80" /></div>
          )}
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 bg-white/90 hover:bg-white rounded-full"><X className="w-5 h-5 text-slate-600" /></button>
          <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
            <div className="flex flex-wrap gap-1.5">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-white/90 text-slate-800">{meta.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${status.cls.replace('bg-', 'bg-white/90 text-')}`}>{status.label}</span>
              {listing.is_featured && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-400 text-amber-950 inline-flex items-center gap-0.5"><Sparkles className="w-3 h-3" /> Featured</span>}
            </div>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <h3 className="text-xl font-bold text-slate-900">{listing.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">by {listing.seller_name} · <span className="capitalize">{listing.seller_type}</span></p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Price</p>
              <p className="text-lg font-bold text-slate-900">${Number(listing.price || 0).toFixed(2)} <span className="text-xs font-normal text-slate-500">{listing.currency}</span></p>
            </div>
            {listing.category && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Category</p>
                <p className="text-sm font-semibold text-slate-800 truncate">{listing.category}</p>
              </div>
            )}
            {listing.stock !== null && listing.stock !== undefined && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Stock</p>
                <p className="text-sm font-semibold text-slate-800">{listing.stock}</p>
              </div>
            )}
            {listing.location && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Location</p>
                <p className="text-sm font-semibold text-slate-800 truncate">{typeof listing.location === 'string' ? listing.location : (listing.location.address || '—')}</p>
              </div>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Description</p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{listing.description || <em className="text-slate-400">No description</em>}</p>
          </div>
          {listing.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {listing.tags.map((tag) => <span key={tag} className="text-[11px] px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">#{tag}</span>)}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-200">
            {listing.status === 'active' && (
              <button
                onClick={() => onFeature(listing._id, !listing.is_featured)}
                disabled={isBusy}
                className={`flex-1 min-w-[140px] flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg font-semibold disabled:opacity-50 ${
                  listing.is_featured
                    ? 'bg-amber-100 hover:bg-amber-200 text-amber-800 ring-1 ring-amber-300'
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white'
                }`}
                data-testid="admin-feature-btn"
              >
                <Sparkles className="w-4 h-4" /> {listing.is_featured ? 'Unfeature' : 'Feature'}
              </button>
            )}
            {listing.status !== 'active' && (
              <button
                onClick={() => onModerate(listing._id, 'approve')}
                disabled={isBusy}
                className="flex-1 min-w-[120px] flex items-center justify-center gap-1.5 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold disabled:opacity-50"
                data-testid="admin-approve-btn"
              >
                {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Approve
              </button>
            )}
            {listing.status !== 'rejected' && (
              <button
                onClick={() => onModerate(listing._id, 'reject')}
                disabled={isBusy}
                className="flex-1 min-w-[120px] flex items-center justify-center gap-1.5 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-semibold disabled:opacity-50"
                data-testid="admin-reject-btn"
              >
                <XCircle className="w-4 h-4" /> Reject
              </button>
            )}
            {listing.status !== 'archived' && (
              <button
                onClick={() => onModerate(listing._id, 'archive')}
                disabled={isBusy}
                className="flex-1 min-w-[120px] flex items-center justify-center gap-1.5 px-4 py-2.5 bg-slate-700 hover:bg-slate-800 text-white rounded-lg font-semibold disabled:opacity-50"
                data-testid="admin-archive-btn"
              >
                <Archive className="w-4 h-4" /> Archive
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminMarketplacePanel() {
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [showCommissionModal, setShowCommissionModal] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const res = await apiCall('/api/admin/marketplace/stats');
      setStats(res);
    } catch (e) { /* non-blocking */ }
  }, []);

  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter === 'all' ? '' : `?status=${encodeURIComponent(statusFilter)}`;
      const res = await apiCall(`/api/admin/marketplace/listings${q}`);
      setListings(res.listings || []);
    } catch (e) {
      toast.error('Failed to load listings', { description: e.message });
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadListings(); }, [loadListings]);

  const moderate = async (listingId, action) => {
    setBusyId(listingId);
    try {
      await apiCall(`/api/admin/marketplace/listings/${listingId}/moderate`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      toast.success(`Listing ${action}d`);
      setSelected(null);
      await Promise.all([loadListings(), loadStats()]);
    } catch (e) {
      toast.error(`Failed to ${action}`, { description: e.message });
    }
    setBusyId(null);
  };

  const toggleFeature = async (listingId, is_featured) => {
    setBusyId(listingId);
    try {
      await apiCall(`/api/admin/marketplace/listings/${listingId}/feature`, {
        method: 'POST',
        body: JSON.stringify({ is_featured }),
      });
      toast.success(is_featured ? 'Listing featured' : 'Listing unfeatured');
      // Update selected/listings without full refetch flicker
      setSelected((prev) => (prev && prev._id === listingId ? { ...prev, is_featured } : prev));
      await Promise.all([loadListings(), loadStats()]);
    } catch (e) {
      toast.error('Failed', { description: e.message });
    }
    setBusyId(null);
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return listings.filter((l) => {
      if (typeFilter !== 'all' && l.type !== typeFilter) return false;
      if (s && !(`${l.title || ''} ${l.seller_name || ''} ${l.category || ''}`.toLowerCase().includes(s))) return false;
      return true;
    });
  }, [listings, typeFilter, search]);

  return (
    <div className="space-y-6" data-testid="admin-marketplace-panel">
      {/* Aurora-style header */}
      <div className="employer-header-glass rounded-2xl overflow-hidden border border-slate-800/40">
        <div className="employer-header-aurora" />
        <div className="relative p-6 flex items-center justify-between text-white">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-white/10 backdrop-blur-md ring-1 ring-white/20">
              <Store className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Marketplace Control Center</h2>
              <p className="text-sm text-white/70">Moderate, feature, and earn commission across all 5 categories</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCommissionModal(true)}
              className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-md ring-1 ring-white/20 text-white text-sm font-semibold flex items-center gap-2 transition"
              data-testid="commission-settings-btn"
            >
              <SettingsIcon className="w-4 h-4" /> Commission · {stats?.commission_pct ?? 5}%
            </button>
            <button
              onClick={() => { loadListings(); loadStats(); }}
              className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-md ring-1 ring-white/20"
              title="Refresh"
              data-testid="admin-mp-refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <PremiumStat icon={<ShoppingBag className="w-5 h-5" />} label="Total Listings" value={stats.total_listings} tone="indigo" />
          <PremiumStat icon={<Clock className="w-5 h-5" />}       label="Pending Review" value={stats.pending_review} tone="amber" hint={stats.pending_review > 0 ? 'Action required' : 'All caught up'} />
          <PremiumStat icon={<CheckCircle className="w-5 h-5" />} label="Active"         value={stats.active}         tone="emerald" />
          <PremiumStat icon={<Sparkles className="w-5 h-5" />}    label="Featured"       value={stats.featured || 0}  tone="rose"   hint="On homepage" />
          <PremiumStat icon={<Tag className="w-5 h-5" />}         label="Orders"         value={stats.total_orders}   tone="violet" hint={`GMV $${Number(stats.gmv || 0).toLocaleString()}`} />
          <PremiumStat icon={<TrendingUp className="w-5 h-5" />}  label="Commission Earned" value={`$${Number(stats.commission_earned || 0).toLocaleString()}`} tone="emerald" hint={`At ${stats.commission_pct}% take`} />
        </div>
      )}

      {/* Filter / search bar */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, seller, category…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              data-testid="admin-mp-search"
            />
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500"><Layers className="inline w-3 h-3 mr-0.5" /> Status</span>
            {[
              { id: 'pending_review', label: 'Pending', count: stats?.pending_review || 0 },
              { id: 'active', label: 'Active' },
              { id: 'rejected', label: 'Rejected' },
              { id: 'archived', label: 'Archived' },
              { id: 'all', label: 'All' },
            ].map((s) => (
              <FilterPill key={s.id} active={statusFilter === s.id} onClick={() => setStatusFilter(s.id)} count={s.id === 'pending_review' ? s.count : 0} testid={`admin-mp-status-${s.id}`}>
                {s.label}
              </FilterPill>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500"><Layers className="inline w-3 h-3 mr-0.5" /> Type</span>
          <FilterPill active={typeFilter === 'all'} onClick={() => setTypeFilter('all')} testid="admin-mp-type-all">All Types</FilterPill>
          {Object.entries(TYPE_META).map(([k, m]) => (
            <FilterPill key={k} active={typeFilter === k} onClick={() => setTypeFilter(k)} testid={`admin-mp-type-${k}`}>
              <m.icon className="w-3.5 h-3.5" /> {m.label}
            </FilterPill>
          ))}
        </div>
      </div>

      {/* Listings */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Store className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No listings match the current filters</p>
            <p className="text-xs text-slate-400 mt-1">Try changing the status or type filter.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="admin-mp-list">
            {filtered.map((l) => {
              const meta = TYPE_META[l.type] || TYPE_META.gear;
              const t = TONE[meta.tone];
              const Icon = meta.icon;
              const isBusy = busyId === l._id;
              const status = STATUS_PILL[l.status] || STATUS_PILL.archived;
              return (
                <div key={l._id} className="group flex items-center gap-4 p-4 hover:bg-slate-50/70 transition" data-testid={`admin-mp-row-${l._id}`}>
                  {/* Thumbnail */}
                  <div className={`relative w-14 h-14 rounded-xl ${t.bg} flex-shrink-0 flex items-center justify-center ring-1 ${t.ring} overflow-hidden`}>
                    {l.images?.[0] ? (
                      <img src={l.images[0]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Icon className={`w-6 h-6 ${t.text}`} />
                    )}
                    {l.is_featured && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 text-amber-950 rounded-full flex items-center justify-center ring-2 ring-white">
                        <Sparkles className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                  {/* Title + meta */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-semibold text-slate-900 truncate" title={l.title}>{l.title}</h4>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${t.bg} ${t.text}`}>{meta.label}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${status.cls}`}>{status.label}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                      by <span className="text-slate-700 font-medium">{l.seller_name}</span>
                      <span className="mx-1.5 text-slate-300">·</span>
                      <span className="capitalize">{l.seller_type}</span>
                      {l.category && <><span className="mx-1.5 text-slate-300">·</span>{l.category}</>}
                    </p>
                  </div>
                  {/* Price */}
                  <div className="text-right flex-shrink-0 hidden sm:block">
                    <p className="text-base font-bold text-slate-900">${Number(l.price || 0).toFixed(2)}</p>
                    {l.stock !== null && l.stock !== undefined && (
                      <p className="text-[10px] text-slate-400">{l.stock} in stock</p>
                    )}
                  </div>
                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setSelected(l)}
                      className="p-2 rounded-lg hover:bg-slate-200 text-slate-600 transition"
                      title="View"
                      data-testid={`admin-mp-view-${l._id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {l.status === 'active' && (
                      <button
                        onClick={() => toggleFeature(l._id, !l.is_featured)}
                        disabled={isBusy}
                        className={`p-2 rounded-lg transition disabled:opacity-40 ${
                          l.is_featured ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'hover:bg-amber-50 text-amber-600'
                        }`}
                        title={l.is_featured ? 'Unfeature' : 'Feature'}
                        data-testid={`admin-mp-feature-${l._id}`}
                      >
                        {l.is_featured ? <Star className="w-4 h-4 fill-amber-500" /> : <Sparkles className="w-4 h-4" />}
                      </button>
                    )}
                    {l.status !== 'active' && (
                      <button
                        onClick={() => moderate(l._id, 'approve')}
                        disabled={isBusy}
                        className="p-2 rounded-lg hover:bg-emerald-100 text-emerald-600 disabled:opacity-40"
                        title="Approve"
                        data-testid={`admin-mp-approve-${l._id}`}
                      >
                        {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      </button>
                    )}
                    {l.status !== 'rejected' && (
                      <button
                        onClick={() => moderate(l._id, 'reject')}
                        disabled={isBusy}
                        className="p-2 rounded-lg hover:bg-rose-100 text-rose-600 disabled:opacity-40"
                        title="Reject"
                        data-testid={`admin-mp-reject-${l._id}`}
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                    {l.status !== 'archived' && (
                      <button
                        onClick={() => moderate(l._id, 'archive')}
                        disabled={isBusy}
                        className="p-2 rounded-lg hover:bg-slate-200 text-slate-500 disabled:opacity-40"
                        title="Archive"
                        data-testid={`admin-mp-archive-${l._id}`}
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <DetailModal listing={selected} onClose={() => setSelected(null)} onModerate={moderate} onFeature={toggleFeature} busyId={busyId} />
      <CommissionSettingsModal
        open={showCommissionModal}
        current={stats?.commission_pct}
        onClose={() => setShowCommissionModal(false)}
        onSaved={loadStats}
      />
    </div>
  );
}
